import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
const PREV_CUSTOMER_KEY = "aisearch_prev_customer";
// /e/:customer/:bID (legacy) and /e/:customer/clip/:key (durable) — the
// single landing point for all email deep-links.
//
// The `clip/:key` form carries base64url(sSourcePath), which resolves by
// clip path and survives GC delete + re-ingest that reassigns bIDs. The
// numeric `:bID` form is kept for any in-flight legacy links.
//
// Always lands on /studio. MainView (grid) does NOT currently read the deep-
// link params, so honoring an "aisearch_view = /" pref here would silently
// drop the deep-link for grid-pref users. Per plan D3a, /studio is the
// canonical landing for the pilot.
//
// Responsibilities:
//   1. Override selectedCustomer when the URL doesn't match the active one
//      (D8: URL wins). Persists the previous slug so StudioView can offer
//      a "switch back" revert via the banner.
//   2. ALWAYS write the PREV_CUSTOMER_KEY (or clear it) so the banner gate
//      reflects the actual current hop. Without this, dismissing the banner
//      on a prior link leaves a stale prev-customer that re-opens the banner
//      with mismatched copy on the next same-customer email link.
//   3. Hand off to /studio via ?evpath=<key> (durable) or ?ev=<bID> (legacy),
//      both with &from=email.
export default function DeepLinkRedirect() {
    const { customer, bID, key } = useParams();
    const { selectedCustomer, setSelectedCustomer, user } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        // Wait for auth context to populate; RequireAuth has already gated
        // unauthenticated users away from this route.
        if (!user)
            return;
        if (!customer || (!bID && !key)) {
            navigate("/", { replace: true });
            return;
        }
        const accessible = user.accessible_customers.some(c => c.slug === customer);
        if (!accessible) {
            // User can't see this customer — bounce home rather than 404 in the
            // SPA. Backend's /api/events/{customer}/{bID} would also 404, but
            // this gives a fast UI path with a hint.
            navigate("/?error=customer_not_accessible", { replace: true });
            return;
        }
        if (customer !== selectedCustomer && selectedCustomer) {
            // Override happening — persist prior slug for the revert button.
            sessionStorage.setItem(PREV_CUSTOMER_KEY, selectedCustomer);
            setSelectedCustomer(customer);
        }
        else {
            // Same customer (or first-load) — clear any stale prior key so a
            // dismissed banner doesn't ghost back into a future hop's UI.
            sessionStorage.removeItem(PREV_CUSTOMER_KEY);
        }
        // Durable clip-path key takes precedence over a legacy numeric bID.
        const target = key
            ? `/studio?evpath=${encodeURIComponent(key)}&from=email`
            : `/studio?ev=${encodeURIComponent(bID)}&from=email`;
        navigate(target, { replace: true });
    }, [user, customer, bID, key, selectedCustomer, setSelectedCustomer, navigate]);
    return _jsx("div", { className: "centered", children: "Loading event\u2026" });
}
